import React, { useState } from "react";
import {
  Box, Drawer as MuiDrawer, AppBar as MuiAppBar, Toolbar, List, CssBaseline,
  IconButton, ListItemButton, ListItemIcon, ListItemText, Menu, MenuItem,
  Typography, Modal, Button, useMediaQuery, Divider, Tooltip, Collapse
} from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { NavLink } from "react-router-dom";
import { useSelector } from "react-redux";

// Icons
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import DashboardIcon from '@mui/icons-material/Dashboard';
import LibraryMusicIcon from '@mui/icons-material/LibraryMusic';
import LyricsIcon from '@mui/icons-material/Lyrics';
import QueueMusicIcon from '@mui/icons-material/QueueMusic';
import MmsIcon from '@mui/icons-material/Mms';
import LogoutIcon from '@mui/icons-material/Logout';
import CampaignIcon from '@mui/icons-material/Campaign';
import RecommendIcon from '@mui/icons-material/Recommend';
import Groups2Icon from '@mui/icons-material/Groups2';
import SquareIcon from '@mui/icons-material/Square';
import { Person } from "@mui/icons-material";

import User from '../user';
import logoCreators from "../../img/logo2.png";
import logoGntunes from "../../img/logo4.svg";

// Constants
const drawerWidth = 260;
const hwset = { height: '20px', width: '20px' };

// Styled Components
const AppBar = styled(MuiAppBar)(({ theme }) => ({
  zIndex: theme.zIndex.drawer + 1
}));

const DrawerHeader = styled("div")(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar
}));

const StyledList = styled(List)({
  '&& .Mui-selected, && .Mui-selected:hover': {
    backgroundColor: 'red',
    '&, & .MuiListItemIcon-root': { color: 'pink' },
  },
  '& .MuiSvgIcon-root': { color: '#FFFFFF' },
  '& .active .MuiListItemButton-root': {
    backgroundColor: '#fff',
    '& .MuiSvgIcon-root, &, & .MuiListItemIcon-root': { color: 'red' },
  },
  '& .MuiListItemButton-root': { color: '#fff' },
  '& .MuiListItemButton-root:hover': {
    backgroundColor: '#FFF',
    '&, & .MuiListItemIcon-root': { color: 'red' },
    '& .MuiSvgIcon-root': { color: 'red' },
  },
});

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 500,
  bgcolor: 'background.paper',
  border: '2px solid #000',
  boxShadow: 24,
  p: 4,
};

const squareIconStyle = {
  transform: 'rotate(45deg)',
  height: '10px',
  width: '10px'
};

export default function Sidebar() {
  const [open, setOpen] = useState(false);
  const [openCollapseItems, setOpenCollapseItems] = useState({});
  const [openModal, setOpenModal] = useState(false);
  const [menuState, setMenuState] = useState({ anchorEl: null, menuRowId: null });

  const userData = useSelector((state) => state.userData.userData);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const LogoApp = process.env.REACT_APP_APPURL === 'https://creator.groovenexus.com' ? logoCreators : logoGntunes;

  // Navigation items configuration
  const getNavItems = () => {
    const baseItems = [
      { id: 1, to: "/dashboard", text: "Dashboard", icon: <DashboardIcon sx={hwset} /> },
      { id: 2, to: "/mylinks", text: "My Links", icon: <LibraryMusicIcon sx={hwset} /> },
      {
        id: 3,
        to: "/songhub",
        text: "Song Hub",
        icon: <LyricsIcon sx={hwset} />,
        hasCollapse: true,
        collapseItems: [
          { id: 4, to: "/songhub", text: "Music", icon: <SquareIcon sx={hwset} style={squareIconStyle} /> },
          { id: 5, to: "/playlist", text: "Playlist", icon: <SquareIcon sx={hwset} style={squareIconStyle} /> },
          { id: 6, to: "/discover-music", text: "Discover Music", icon: <SquareIcon sx={hwset} style={squareIconStyle} /> },
          { id: 7, to: "/discover-playlist", text: "Discover Playlist", icon: <SquareIcon sx={hwset} style={squareIconStyle} /> },
        ]
      },
    ];

    // Add beta features
    // if (userData?.beta) {
    //   baseItems.push({ id: 9, to: "/campaign", text: "Campaign", icon: <CampaignIcon sx={hwset} /> });
    // }

    // Add distribution features
    if (userData?.enable_distribution) {
      const distributionItems = [
        { id: 9, to: "/campaign?tab=released", text: "Campaign", icon: <CampaignIcon sx={hwset} /> },
        { id: 10, to: "/recommendations", text: "Recommendation", icon: <RecommendIcon sx={hwset} /> },
        {
          id: 11,
          to: "/importrevenue",
          text: "Collaborate",
          icon: <Groups2Icon sx={hwset} />,
          hasCollapse: true,
          collapseItems: [
            { id: 16, to: "/collaboration", text: "Collaborate Now", icon: <SquareIcon sx={hwset} style={squareIconStyle} /> },
            { id: 17, to: "/collab-request", text: "Collaboration Request", icon: <SquareIcon sx={hwset} style={squareIconStyle} /> },
          ],
        },
        { id: 12, to: "/submitdemo", text: "Demo", icon: <QueueMusicIcon sx={hwset} /> },
        { id: 14, to: "/studioassociatelist", text: "Studio Associate", icon: <Person sx={hwset} /> },
        { id: 16, to: "/coverImage", text: "Cover Image", icon: <MmsIcon sx={hwset} /> },
      ];
      baseItems.push(...distributionItems);
    }

    // Always add logout at the end
    baseItems.push({
      id: 999,
      to: "/logout",
      text: "Logout",
      icon: <LogoutIcon sx={hwset} style={{ transform: 'rotate(180deg)' }} />
    });

    return baseItems;
  };

  const navItems = getNavItems();

  // Event handlers
  const handleClickMenu = (event, item) => {
    if (item.hasCollapse) {
      if (open) {
        // Toggle collapse when sidebar is open
        setOpenCollapseItems(prev => ({
          ...prev,
          [item.id]: !prev[item.id]
        }));
      } else {
        // Show menu when sidebar is closed
        setMenuState({ anchorEl: event.currentTarget, menuRowId: item.id });
      }
    }
  };

  const handleCloseMenu = () => {
    setMenuState({ anchorEl: null, menuRowId: null });
  };

  const toggleDrawer = () => {
    setOpen(prev => !prev);
    handleCloseMenu();
  };

  const handleNavItemClick = () => {
    if (isMobile) setOpen(false);
    handleCloseMenu();
  };

  // Render functions
  const renderListItem = (item) => (
    <ListItemButton
      className={`${open ? '' : 'pb-0'} rounded-3`}
      onClick={(e) => handleClickMenu(e, item)}
      sx={{
        minHeight: 48,
        paddingTop: 0,
        marginTop: '10px !important',
        justifyContent: open ? 'initial' : 'center',
        flexDirection: open ? '' : 'column',
        alignItems: open ? '' : 'center',
        px: 2.5,
      }}
    >
      <Tooltip title={item.text} placement="right" arrow>
        <ListItemIcon sx={{
          minWidth: 0,
          mr: open ? 3 : '0px',
          justifyContent: 'center',
        }}>
          {item.icon}
          {(item?.hasCollapse && !open) && (
            <ChevronRightIcon sx={{
              position: 'absolute',
              right: '-7px',
              top: '14px',
              width: '20px',
              height: '20px'
            }} />
          )}
        </ListItemIcon>
      </Tooltip>
      {(isMobile || open) && (
        <ListItemText
          primary={
            <Typography
              variant="span"
              sx={!open ? {
                fontSize: '11px',
                display: item.text.length > 10 ? 'block' : 'inline-block',
                whiteSpace: item.text.length > 15 ? 'break-spaces' : 'nowrap',
                textAlign: 'center',
              } : {}}
            >
              {item.text}
            </Typography>
          }
        />
      )}
      {(item?.hasCollapse && open) && (
        openCollapseItems[item.id] ? <ExpandLess /> : <ExpandMore />
      )}
    </ListItemButton>
  );

  const renderNavItem = (item) => (
    <React.Fragment key={item.id}>
      {item?.hasCollapse ? (
        <div>
          {renderListItem(item)}
          {!isMobile && (
            <Menu
              id={`basic-menu-${item.id}`}
              anchorEl={menuState.anchorEl}
              open={!open && menuState.menuRowId === item.id}
              onClose={handleCloseMenu}
              anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
              MenuListProps={{ 'aria-labelledby': `basic-button-${item.id}` }}
            >
              {item.collapseItems.map((option) => (
                <MenuItem key={option.id} className="p-0" onClick={handleCloseMenu}>
                  <NavLink to={option.to} className="nav-list-items py-1">
                    <ListItemButton className="tab-items rounded-3 ps-0">
                      <ListItemIcon className="justify-content-center align-items-center">
                        {option.icon}
                      </ListItemIcon>
                      <ListItemText primary={<Typography variant="span">{option.text}</Typography>} />
                    </ListItemButton>
                  </NavLink>
                </MenuItem>
              ))}
            </Menu>
          )}
        </div>
      ) : (
        <NavLink to={item.to} className='nav-list-items' onClick={handleNavItemClick}>
          {renderListItem(item)}
        </NavLink>
      )}

      {(item?.hasCollapse && open) && (
        <Collapse in={openCollapseItems[item.id]} timeout={300} unmountOnExit>
          {item.collapseItems.map((collapseItem) => (
            <NavLink key={collapseItem.id} to={collapseItem.to} className="nav-list-items" onClick={handleNavItemClick}>
              <ListItemButton className="tab-items my-2 rounded-3">
                <ListItemIcon className="p-2 justify-content-center align-items-center">
                  {collapseItem.icon}
                </ListItemIcon>
                <ListItemText primary={collapseItem.text} />
              </ListItemButton>
            </NavLink>
          ))}
        </Collapse>
      )}
    </React.Fragment>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar className='bg-white shadow-none border' position="fixed">
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={toggleDrawer}
            className="text-black ms-1"
            edge="start"
          >
            <MenuOpenIcon />
          </IconButton>
          <Box sx={{ marginLeft: 4 }}>
            <img src={LogoApp} className="w-100" alt="Logo" />
          </Box>
          <Box className='d-flex align-items-center gap-5 w-100 justify-content-end'>
            <User />
          </Box>
        </Toolbar>
      </AppBar>

      <MuiDrawer
        variant={isMobile ? "temporary" : "permanent"}
        open={open}
        onClose={() => setOpen(false)}
        sx={{
          flexShrink: 0,
          width: isMobile ? drawerWidth : (open ? drawerWidth : 70),
          whiteSpace: "nowrap",
          transition: theme.transitions.create("width", {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen
          }),
          "& .MuiDrawer-paper": {
            width: isMobile ? drawerWidth : (open ? drawerWidth : 70),
            marginTop: isMobile ? 0 : '4rem',
            padding: "10px",
            boxSizing: "border-box",
            background: 'linear-gradient(180deg, #ED1C24 0%, #AA0066 100%)',
            boxShadow: '0px 4px 15px rgba(0, 0, 0, 0.55)',
            transition: theme.transitions.create("width", {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen
            })
          },
        }}
        ModalProps={{ keepMounted: true }}
      >
        {isMobile && (
          <DrawerHeader>
            <IconButton onClick={handleNavItemClick}>
              <ChevronLeftIcon fontSize="large" sx={{ color: "white" }} />
            </IconButton>
          </DrawerHeader>
        )}
        <Divider sx={{ color: "white", display: isMobile ? 'block' : 'none' }} />

        <StyledList sx={{ padding: '0px' }}>
          <List
            sx={{ width: "100%", maxWidth: 360, paddingBottom: '5rem !important' }}
            className="p-0"
            component="nav"
          >
            {navItems.map(renderNavItem)}
          </List>
        </StyledList>

      </MuiDrawer>

    </Box>
  );
}